import { parseCsvRows } from '../dataset/metadata-json/dataset-metadata-template.util';
import type {
  ModelBasicInfo,
  ModelMetadataInfo,
  ModelOnboardingPayload,
} from './model.models';
import { additionalInfoMutualExclusionError } from '../shared/additional-info/additional-info.logic';

export const MODEL_METADATA_TYPE = 'adex:AiModel' as const;

export interface ModelMetadataTemplateJson {
  type: string[];
  title: string;
  permission: 'Open' | 'Restricted';
  shortDescription: string;
  longDescription: string;
  license: string;
  otherLicense?: string;
  industry: string;
  datasetType: string;
  fileFormat: string;
  tags: string[];
}

const PERMISSION_VALUES = ['Open', 'Restricted'] as const;

export function buildModelMetadataTemplate(): ModelMetadataTemplateJson {
  return {
    type: [MODEL_METADATA_TYPE],
    title: '',
    permission: 'Open',
    shortDescription:
      'Brief description of the model (max 200 characters). This provides a concise overview of what the model does and its primary purpose.',
    longDescription:
      'Detailed description of the model (max 2000 characters). Include architecture, training methodology, use cases, and performance metrics.',
    license: 'MIT',
    otherLicense: '',
    industry: 'Agriculture',
    datasetType: 'Structured',
    fileFormat: 'CSV',
    tags: ['machine-learning', 'agriculture', 'prediction'],
  };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowToCsvLine(cells: Array<string | number>): string {
  return cells.map(escapeCsvCell).join(',');
}

export function buildModelMetadataTemplateCsv(): string {
  const t = buildModelMetadataTemplate();
  const lines: string[] = [
    '# Model metadata template — fill the value column (one field per row).',
    rowToCsvLine(['field', 'value', 'notes']),
    rowToCsvLine([
      'type',
      MODEL_METADATA_TYPE,
      `Must be ${MODEL_METADATA_TYPE}`,
    ]),
    rowToCsvLine([
      'title',
      t.title,
      'Model display name (required). Max 200 characters.',
    ]),
    rowToCsvLine(['permission', t.permission, PERMISSION_VALUES.join(' | ')]),
    rowToCsvLine([
      'shortDescription',
      t.shortDescription,
      'Max 200 characters.',
    ]),
    rowToCsvLine([
      'longDescription',
      t.longDescription,
      'Max 2000 characters.',
    ]),
    rowToCsvLine(['license', t.license, 'e.g. MIT, CC-BY 4.0, Other']),
    rowToCsvLine([
      'otherLicense',
      t.otherLicense ?? '',
      'Required only when license is Other.',
    ]),
    rowToCsvLine(['industry', t.industry, 'e.g. Agriculture, Forest, Water']),
    rowToCsvLine([
      'datasetType',
      t.datasetType,
      'Model type (e.g. Structured)',
    ]),
    rowToCsvLine(['fileFormat', t.fileFormat, 'e.g. CSV, JSON, PKL']),
    rowToCsvLine([
      'tags',
      t.tags.join(';'),
      'Separate multiple tags with semicolons (;)',
    ]),
  ];
  return lines.join('\r\n');
}

function downloadTextFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadModelMetadataTemplate(
  filename = 'model-metadata-template.json',
): void {
  const jsonString = JSON.stringify(buildModelMetadataTemplate(), null, 2);
  downloadTextFile(jsonString, filename, 'application/json');
}

export function downloadModelMetadataTemplateCsv(
  filename = 'model-metadata-template.csv',
): void {
  downloadTextFile(
    buildModelMetadataTemplateCsv(),
    filename,
    'text/csv;charset=utf-8',
  );
}

function parseVerticalModelMetadataCsv(
  rows: string[][],
): Record<string, unknown> {
  const meta: Record<string, string> = {};

  for (const row of rows) {
    const c0 = (row[0] ?? '').trim();
    if (!c0 || c0.startsWith('#')) {
      continue;
    }
    const c0Lower = c0.toLowerCase();
    if (c0Lower === 'field') {
      continue;
    }
    meta[c0] = (row[1] ?? '').trim();
  }

  const tagsRaw = meta['tags'] ?? '';
  const tags = tagsRaw
    ? tagsRaw
        .split(/[;|]/)
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  const typeRaw = meta['type'] || MODEL_METADATA_TYPE;

  return {
    type: [typeRaw.includes('adex:') ? typeRaw : MODEL_METADATA_TYPE],
    title: meta['title'] ?? '',
    permission: meta['permission'] || 'Open',
    shortDescription: meta['shortDescription'] ?? '',
    longDescription: meta['longDescription'] ?? '',
    license: meta['license'] ?? '',
    otherLicense: meta['otherLicense'] ?? '',
    industry: meta['industry'] ?? '',
    datasetType: meta['datasetType'] ?? '',
    fileFormat: meta['fileFormat'] ?? '',
    tags,
  };
}

/** Convert filled CSV template to the JSON shape used by validation / onboarding. */
export function parseModelMetadataCsv(
  csvText: string,
): Record<string, unknown> {
  const rows = parseCsvRows(csvText).filter((row) => {
    const first = (row[0] ?? '').trim();
    return first && !first.startsWith('#');
  });
  if (rows.length < 2) {
    throw new Error(
      'Your CSV file looks empty. Download the template, add your model details, and upload again.',
    );
  }

  const firstHeader = (rows[0][0] ?? '').trim().toLowerCase();
  if (firstHeader === 'field') {
    return parseVerticalModelMetadataCsv(rows);
  }

  throw new Error(
    'We could not read this CSV file. Download the template and use the field and value columns.',
  );
}

export function validateModelMetadataJson(data: Record<string, unknown>): {
  valid: boolean;
  missingFields: string[];
  error?: string;
} {
  const missingFields: string[] = [];

  const type = data['type'];
  const typeArray = Array.isArray(type) ? type : type ? [type] : [];
  if (!typeArray.includes(MODEL_METADATA_TYPE)) {
    return {
      valid: false,
      missingFields,
      error: `Template must include type ["${MODEL_METADATA_TYPE}"]`,
    };
  }

  const bothErr = additionalInfoMutualExclusionError(data);
  if (bothErr) {
    return { valid: false, missingFields, error: bothErr };
  }

  const title = String(data['title'] ?? data['name'] ?? '').trim();
  if (!title) {
    missingFields.push('Model title');
  }

  const permission = data['permission'];
  if (
    permission !== 'Open' &&
    permission !== 'Restricted' &&
    permission !== 'OPEN' &&
    permission !== 'PRIVATE'
  ) {
    missingFields.push('Access permission (choose Open or Restricted)');
  }

  const stringRequired: Array<{ key: string; label: string }> = [
    { key: 'shortDescription', label: 'Short description' },
    { key: 'longDescription', label: 'Long description' },
    { key: 'license', label: 'License' },
    { key: 'industry', label: 'Industry' },
    { key: 'datasetType', label: 'Model type' },
    { key: 'fileFormat', label: 'File format' },
  ];

  for (const { key, label } of stringRequired) {
    const value = data[key];
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      missingFields.push(label);
    }
  }

  const tags = data['tags'];
  if (!Array.isArray(tags) || tags.length === 0) {
    missingFields.push('At least one tag');
  } else {
    const tooLong = (tags as unknown[])
      .map((t) => String(t).trim())
      .filter((t) => t.length > 20);
    if (tooLong.length > 0) {
      return {
        valid: false,
        missingFields: [
          `Each tag must be 20 characters or fewer (too long: ${tooLong.join(', ')})`,
        ],
      };
    }
  }

  if (missingFields.length > 0) {
    return { valid: false, missingFields };
  }

  return { valid: true, missingFields: [] };
}

function normalizeTags(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw
      .split(/[,;|]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

function str(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

/** Map uploaded JSON to wizard payload (title/permission may come from JSON or defaults). */
export function parseModelMetadataToOnboarding(
  data: Record<string, unknown>,
): ModelOnboardingPayload {
  const validation = validateModelMetadataJson(data);
  if (!validation.valid) {
    throw new Error(
      validation.error ??
        `Missing required fields: ${validation.missingFields.join(', ')}`,
    );
  }

  const permissionRaw = str(data['permission']) || 'Open';
  const permission =
    permissionRaw === 'Restricted' || permissionRaw.toUpperCase() === 'PRIVATE'
      ? 'Restricted'
      : ('Open' as const);

  const basic: ModelBasicInfo = {
    title: str(data['title'] ?? data['name']),
    permission,
    shortDescription: str(data['shortDescription']),
    longDescription: str(data['longDescription']),
  };

  const metadata: ModelMetadataInfo = {
    industry: str(data['industry']),
    license: str(data['license']),
    otherLicense: str(data['otherLicense']),
    modelType: str(data['datasetType'] ?? data['modelType']),
    fileFormat: str(data['fileFormat']),
    tags: normalizeTags(data['tags']),
  };

  return { basic, metadata };
}
